import React, { useEffect, useId, useRef, useState } from 'react';
import {
  STUDENT_NOTE_MAX, cleanStudentNote, makeStudentNote, canDeleteStudentNote, formatNoteDate,
} from '../utils/studentNotes';
import { addStudentNote, deleteStudentNote } from '../services/studentNotes';

/**
 * Student-level teacher notes (v3.96.0) — the "Teacher notes" section of the
 * student pop-up (StudentDetailsModal, opened from the Dashboard and from
 * Letter/Sound, NWF and PA 4K Results). Dated entries, newest first, each with who wrote it;
 * add one, delete your own. For a 4K student the 4K Readiness note is shown
 * here too, read-only. The whole section is `.no-print`: the pop-up's Print
 * Report never carries it. See src/utils/studentNotes.js.
 *
 * Saving follows the app's rule for spinners (CLAUDE.md): a write not
 * confirmed in 15 s gives the controls back with a message; a late result
 * still lands on screen while the pop-up is open.
 */

// v3.98.0: notes print only on a staff copy of the Student Report (Reports →
// Student Report, "Include teacher notes"), never on the family report.
export const STUDENT_NOTES_HINT = 'Teacher notes: not sent to families. They print only on a staff copy of the Student Report. Write them as you would for the student’s school file.';
export const STUDENT_NOTE_SAVE_TIMEOUT_MS = 15000;
export const STUDENT_NOTE_SLOW_MESSAGE = 'Not confirmed yet: the connection seems slow or offline. The note will still save when the connection comes back, as long as this page stays open.';
export const STUDENT_NOTES_DEMO_REASON = 'Notes cannot be changed on the demo.';
export const STUDENT_NOTE_UNSAVED_MESSAGE = 'This note is not saved yet. Press Add note to keep it.';
export const STUDENT_NOTES_LOAD_FAILED = 'Could not load this student\u2019s notes. Close and reopen this window to try again.';
const EMPTY = [];

const box = {
  border: '1px solid #f59e0b', background: '#fffbeb', borderRadius: '8px', padding: '12px 14px', margin: '16px 0', textAlign: 'left',
};
const buttonStyle = {
  minHeight: '44px', padding: '0 14px', borderRadius: '6px', fontSize: '1em', cursor: 'pointer',
  border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b',
};
const primaryButtonStyle = { ...buttonStyle, background: '#b45309', borderColor: '#b45309', color: '#fff', fontWeight: 600 };
const smallButtonStyle = {
  padding: '2px 10px', fontSize: '0.85em', borderRadius: '4px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', boxShadow: 'none',
};

// The row a result is about, matched by attribute value (never a built
// selector — an id is data, whatever characters it holds).
const rowOf = (entry) => (el) => {
  const row = el && el.closest ? el.closest('[data-note-id]') : null;
  return !!row && row.getAttribute('data-note-id') === entry.id;
};

/** "Delete your note from Oct 30, 2026 at 3:00 PM" (an admin: "Delete Mr. Aide's note from …"). */
function deleteLabel(entry, user) {
  const d = new Date(entry.createdAt);
  const time = Number.isNaN(d.getTime()) ? '' : ` at ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  const whose = user && entry.authorUid === user.uid ? 'your' : (entry.authorName ? `${entry.authorName}\u2019s` : 'this');
  return `Delete ${whose} note from ${formatNoteDate(entry.createdAt)}${time}`;
}

function StudentTeacherNotes({
  studentId, studentName, entries: initialEntries = EMPTY, user, readOnly = false, readOnlyReason = STUDENT_NOTES_DEMO_REASON,
  readinessNote = '', onEntriesChange, onGuardChange, loadStatus = 'loaded',
}) {
  const uid = useId();
  const [entries, setEntries] = useState(initialEntries);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', tone: 'error' });
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const mounted = useRef(false);
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  // An add still on its way after the timeout: retrying the same text reuses
  // this entry, and arrayUnion never adds an identical entry twice.
  const pendingEntry = useRef(null);
  const headingRef = useRef(null);
  const keepRef = useRef(null);
  const textRef = useRef(null);
  // Only the latest add drives "Saving…" and the status line.
  const attempts = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // A fresher copy of the student record arrives after the pop-up opens.
  useEffect(() => { setEntries(initialEntries); }, [initialEntries]);

  // Focus "Keep" when a delete asks first — the Delete button that was
  // pressed has just gone.
  useEffect(() => {
    if (confirmId && keepRef.current) keepRef.current.focus();
  }, [confirmId]);

  // Tell the page what changed (after the commit, not inside an updater).
  useEffect(() => {
    // A prop sync is not a local change (and StrictMode re-runs effects).
    if (entries === initialEntries) return;
    if (onEntriesChange) onEntriesChange(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  // The pop-up's own Escape (anywhere in it, focus or not) must not close it
  // from under an unsaved note or an open "Delete this note?".
  // A delete question only counts while its note is still listed — and is
  // dropped when the note leaves, so it never comes back by itself.
  const confirmOpen = !!confirmId && entries.some((e) => e.id === confirmId);
  useEffect(() => {
    if (confirmId && !entries.some((e) => e.id === confirmId) && busyId !== confirmId) setConfirmId(null);
  }, [entries, confirmId, busyId]);
  const guarded = Boolean(cleanStudentNote(draft) || confirmOpen || busyId || saving);
  useEffect(() => {
    if (onGuardChange) onGuardChange(guarded);
  }, [guarded, onGuardChange]);
  useEffect(() => () => { if (onGuardChange) onGuardChange(false); }, [onGuardChange]);

  const applyEntries = (fn) => setEntries((prev) => fn(prev));
  const addButtonRef = useRef(null);
  // The note box is disabled while saving, so focus goes back to it after the
  // re-render that enables it.
  const [refocusBox, setRefocusBox] = useState(false);
  useEffect(() => {
    if (refocusBox && !saving && textRef.current) {
      textRef.current.focus();
      setRefocusBox(false);
    }
  }, [refocusBox, saving]);
  const shouldRefocus = (owner) => {
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    return !active || active === document.body || active === owner || active === textRef.current;
  };
  // Move focus to `el` only when it was lost, or still sits on a control this
  // result is about (`owner`) — never away from what the teacher moved on to.
  const focusIfLost = (el, owner) => {
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const lost = !active || active === document.body;
    const onOwner = !!(active && owner && !lost && (active === owner || (typeof owner === 'function' && owner(active))));
    if (el && (lost || onOwner)) el.focus();
  };

  const add = async () => {
    const text = cleanStudentNote(draft);
    if (!text || saving) return;
    const entry = pendingEntry.current && pendingEntry.current.text === text ? pendingEntry.current : makeStudentNote(text, user);
    pendingEntry.current = entry;
    attempts.current += 1;
    const attempt = attempts.current;
    const latest = () => mounted.current && attempt === attempts.current;
    setSaving(true);
    setMessage({ text: '', tone: 'error' });
    let settled = false;
    const timer = setTimeout(() => {
      if (settled || !latest()) return;
      setSaving(false);
      setMessage({ text: STUDENT_NOTE_SLOW_MESSAGE, tone: 'error' });
      if (shouldRefocus(addButtonRef.current)) setRefocusBox(true);
    }, STUDENT_NOTE_SAVE_TIMEOUT_MS);
    try {
      await addStudentNote(user, studentId, entry);
      settled = true;
      clearTimeout(timer);
      if (pendingEntry.current === entry) pendingEntry.current = null;
      if (!mounted.current) return;
      // A saved entry always shows, whichever attempt it was.
      applyEntries((prev) => [entry, ...prev.filter((e) => e.id !== entry.id)]);
      if (cleanStudentNote(latestDraft.current) === text) setDraft('');
      if (!latest()) return;
      setSaving(false);
      setMessage({ text: 'Note added.', tone: 'ok' });
    } catch (err) {
      settled = true;
      clearTimeout(timer);
      if (!latest()) return;
      setSaving(false);
      setMessage({ text: (err && err.userMessage) || 'Could not save the note. Check your connection and try again.', tone: 'error' });
      if (shouldRefocus(addButtonRef.current)) setRefocusBox(true);
    }
  };

  const remove = async (entry) => {
    if (busyId) return;
    setBusyId(entry.id);
    setMessage({ text: '', tone: 'error' });
    let settled = false;
    // Clear only what still belongs to this entry: by the time a late result
    // lands the teacher may be typing, or asking about another note.
    const clearMine = () => {
      setBusyId((b) => (b === entry.id ? null : b));
      setConfirmId((c) => (c === entry.id ? null : c));
    };
    const timer = setTimeout(() => {
      if (settled || !mounted.current) return;
      clearMine();
      setMessage({ text: STUDENT_NOTE_SLOW_MESSAGE.replace('The note will still save', 'The delete will still go through'), tone: 'error' });
      focusIfLost(headingRef.current, rowOf(entry));
    }, STUDENT_NOTE_SAVE_TIMEOUT_MS);
    try {
      await deleteStudentNote(user, studentId, entry);
      settled = true;
      clearTimeout(timer);
      if (!mounted.current) return;
      applyEntries((prev) => prev.filter((e) => e.id !== entry.id));
      clearMine();
      setMessage({ text: 'Note deleted.', tone: 'ok' });
      focusIfLost(headingRef.current, rowOf(entry));
    } catch (err) {
      settled = true;
      clearTimeout(timer);
      if (!mounted.current) return;
      setBusyId((b) => (b === entry.id ? null : b));
      setMessage({ text: (err && err.userMessage) || 'Could not delete the note. Check your connection and try again.', tone: 'error' });
      focusIfLost(keepRef.current || headingRef.current, rowOf(entry));
    }
  };

  // Escape inside this section never closes the pop-up from under a draft or
  // an open "Delete this note?": it answers Keep, or says the note is unsaved.
  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (confirmOpen) {
      e.stopPropagation();
      // While that delete is already running, Escape changes nothing.
      if (busyId === confirmId) return;
      setConfirmId(null);
      if (headingRef.current) headingRef.current.focus();
    } else if (cleanStudentNote(draft)) {
      e.stopPropagation();
      if (!saving) setMessage({ text: STUDENT_NOTE_UNSAVED_MESSAGE, tone: 'error' });
    }
  };

  const textId = `${uid}-text`;
  const titleId = `${uid}-title`;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section className="no-print student-teacher-notes" aria-labelledby={titleId} style={box} onKeyDown={onKeyDown}>
      <h3 id={titleId} ref={headingRef} tabIndex={-1} style={{ margin: '0 0 4px', color: '#92400e' }}>Teacher notes</h3>
      <p style={{ margin: '0 0 10px', color: '#92400e', fontSize: '0.9em', fontStyle: 'italic' }}>{STUDENT_NOTES_HINT}</p>

      {readinessNote && (
        <div style={{ margin: '0 0 10px', padding: '8px 10px', background: '#fff', border: '1px solid #fde68a', borderRadius: '6px' }}>
          <div style={{ fontSize: '0.85em', fontWeight: 700, color: '#475569' }}>4K Readiness note (beginning of year)</div>
          <p style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{readinessNote}</p>
        </div>
      )}

      {loadStatus === 'failed' && (
        <p role="alert" style={{ margin: '0 0 10px', color: '#b91c1c' }}>{STUDENT_NOTES_LOAD_FAILED}</p>
      )}
      {entries.length === 0 ? (
        loadStatus === 'loaded' ? <p style={{ margin: '0 0 10px', color: '#64748b' }}>No notes yet.</p>
          : (loadStatus === 'loading' ? <p style={{ margin: '0 0 10px', color: '#64748b' }}>Loading notes…</p> : null)
      ) : (
        <ul aria-label={`Teacher notes for ${studentName}`} style={{ listStyle: 'none', margin: '0 0 10px', padding: 0 }}>
          {entries.map((e) => (
            <li key={e.id} data-note-id={e.id} style={{ padding: '8px 0', borderTop: '1px solid #fde68a' }}>
              <div style={{ fontSize: '0.85em', color: '#475569', fontWeight: 600 }}>
                {[formatNoteDate(e.createdAt), e.authorName].filter(Boolean).join(' · ')}
              </div>
              <p style={{ margin: '2px 0 4px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.text}</p>
              {!readOnly && canDeleteStudentNote(e, user) && (
                confirmId === e.id ? (
                  <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.85em' }}>Delete this note?</span>
                    <button type="button" style={smallButtonStyle} disabled={busyId === e.id} onClick={() => remove(e)}>
                      {busyId === e.id ? 'Deleting…' : 'Yes, delete'}
                    </button>
                    <button type="button" ref={keepRef} style={smallButtonStyle} disabled={busyId === e.id} onClick={() => { setConfirmId(null); if (headingRef.current) headingRef.current.focus(); }}>Keep</button>
                  </span>
                ) : (
                  <button type="button" style={smallButtonStyle} aria-label={deleteLabel(e, user)} onClick={() => setConfirmId(e.id)}>Delete</button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {readOnly ? (
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9em' }}>{readOnlyReason}</p>
      ) : (
        <div>
          <label htmlFor={textId} style={{ display: 'block', fontWeight: 600, margin: '0 0 4px' }}>New note</label>
          <textarea
            id={textId}
            ref={textRef}
            value={draft}
            maxLength={STUDENT_NOTE_MAX}
            rows={3}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            placeholder="e.g. Leaves out the first sound; practice blending."
            style={{ width: '100%', boxSizing: 'border-box', fontSize: '16px', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
            <button type="button" ref={addButtonRef} onClick={add} disabled={saving || !cleanStudentNote(draft)} style={primaryButtonStyle}>
              {saving ? 'Saving…' : 'Add note'}
            </button>
            <span style={{ color: '#64748b', fontSize: '0.85em' }}>{draft.length}/{STUDENT_NOTE_MAX}</span>
          </div>
        </div>
      )}
      <p role="status" style={{ margin: message.text ? '8px 0 0' : 0, color: message.tone === 'ok' ? '#166534' : '#b91c1c', fontSize: '0.95em' }}>{message.text}</p>
    </section>
  );
}

export default StudentTeacherNotes;
